import type { CustomHeaderProps } from 'ag-grid-react'
import { Info } from 'lucide-react'
import { DagColumnHeaderLabel } from './DagColumnHeaderLabel'
import { useColumnTotalsStore } from '@/stores/useColumnTotalsStore'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface ColumnTotal {
  id: string
  label: string
  format: string
  value: unknown
}

// Aggregates use different conventions than row cells:
// - percent values are already in percentage points (43.74, not 0.4374)
// - number values should not force trailing zeros (1245, not 1,245.00)
// See docs/architecture/dag-table-aggregates-frontend.md §5.
function formatAggregateValue(value: unknown, format: string): string {
  if (value === null || value === undefined || value === '') return '-'
  const num = typeof value === 'number' ? value : parseFloat(String(value))
  if (isNaN(num)) return String(value)

  switch (format) {
    case 'currency':
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case 'currency_3dp':
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })
    case 'percent':
      return `${num.toFixed(2)}%`
    case 'integer':
      return Math.round(num).toLocaleString('en-US')
    case 'number':
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 })
    default:
      return String(value)
  }
}

interface DagColumnHeaderWithTotalsProps extends CustomHeaderProps {
  totals: ColumnTotal[]
  /** When set, live totals are read from useColumnTotalsStore under this key. */
  totalsColumnId?: string
  isRequiredInput?: boolean
  isReorderable?: boolean
}

export function DagColumnHeaderWithTotals(
  props: DagColumnHeaderWithTotalsProps
) {
  const { displayName, totalsColumnId, isRequiredInput, isReorderable } = props
  const liveTotals = useColumnTotalsStore(s =>
    totalsColumnId ? s.totals[totalsColumnId] : undefined
  )
  const totals = totalsColumnId ? (liveTotals ?? []) : props.totals
  const first = totals[0]
  const hasMore = totals.length > 1

  return (
    <div className="flex h-full min-w-0 flex-col items-start justify-center py-1 leading-tight">
      <span className="flex min-w-0 max-w-full text-[13px]">
        <DagColumnHeaderLabel
          displayName={displayName}
          isRequiredInput={isRequiredInput}
          isReorderable={isReorderable}
        />
      </span>
      {first && (
        <span className="flex items-center gap-1 truncate text-[11px] font-semibold text-[#CF1F2E]">
          <span className="truncate" title={first.label}>
            {formatAggregateValue(first.value, first.format)}
          </span>
          {hasMore && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 shrink-0 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <ul className="space-y-1 text-xs">
                    {totals.map(t => (
                      <li key={t.id} className="flex justify-between gap-4">
                        <span>{t.label}</span>
                        <span className="font-semibold">
                          {formatAggregateValue(t.value, t.format)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      )}
    </div>
  )
}
